import { useTranslation } from 'react-i18next'

import { Moon24, Sun24 } from '@makyoapp/makyo-icon/icon'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useBuilderAccordionContext } from '@/context/__project__/BuilderAccordionContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { useActiveDevice } from '@/hooks/useActiveDevice'
import {
  BuilderComponent,
  BuilderComponentSetting,
  BuilderComponentSettingType,
  BuilderDependOn,
  ComponentSettingKey,
  FlattenedItem,
} from '@/shared/builder'
import { BUILDER_DEVICES } from '@/shared/builder-constants'
import { hasColorAdaptiveSettings, hasResponsiveSettings } from '@/shared/builder/viewport/responsive-settings'
import { Accordion, AccordionItem } from '@/components/Accordion/Accordion'

import ComponentSettingField from './ComponentSettingField'

interface ComponentSettingsAccordionProps {
  activeComponentId: string
  comp: BuilderComponent
  flattenedSettings: BuilderComponentSetting[]
  flattenedComponents: FlattenedItem[]
  wrap?: boolean
  settings: Record<
    string,
    {
      dependOn?: BuilderDependOn
      items: BuilderComponentSetting[]
    }
  >
  name: 'design' | 'data' | 'behavior' | 'action'
  ancestors: FlattenedItem[]
}

function hasColorAdaptiveSettingsRecursive(item: BuilderComponentSetting): boolean {
  if (hasColorAdaptiveSettings(item.id)) {
    return true
  }

  if (item.type === BuilderComponentSettingType.Group && item.groupItems) {
    return Object.values(item.groupItems).some((groupItems) =>
      groupItems.some((subItem) => hasColorAdaptiveSettingsRecursive(subItem))
    )
  }

  return false
}

function hasResponsiveSettingsRecursive(item: BuilderComponentSetting): boolean {
  if (hasResponsiveSettings(item.id)) {
    return true
  }

  if (item.type === BuilderComponentSettingType.Group && item.groupItems) {
    return Object.values(item.groupItems).some((groupItems) =>
      groupItems.some((subItem) => hasResponsiveSettingsRecursive(subItem))
    )
  }

  return false
}

/**
 * ComponentSettingsAccordion is a function component that provides an interface for editing the settings of a component in a builder.
 */
function ComponentSettingsAccordion({
  activeComponentId,
  comp,
  flattenedSettings,
  flattenedComponents,
  settings,
  wrap = true,
  name,
  ancestors,
}: ComponentSettingsAccordionProps) {
  const stgs = Object.entries(settings)

  const { t } = useTranslation()

  const [device] = useActiveDevice()
  const ActiveDeviceIcon = BUILDER_DEVICES[device].Icon
  const [scheme] = useBuilderColorSchemeV2()
  const mode = scheme.current

  const ModeIcon = mode === 'dark' ? <Moon24 className='w-3.5 h-3.5' /> : <Sun24 className='w-3.5 h-3.5' />

  const { setAccordions, accordions } = useBuilderAccordionContext()
  const { activeComponentAccordion } = useAppBuilderContext()
  const { activeComponent, activeLayerElementSettings } = useActiveLayerContext()
  const elSetting = !activeComponent ? undefined : activeLayerElementSettings?.[activeComponent.id]
  const items = (
    <>
      {stgs.map(([group, { dependOn, items }], i) => {
        const showWhen = dependOn?.showWhen
        const dependId = dependOn?.id
        const dependValue = dependId ? elSetting?.[dependId] : undefined
        const shouldShow = showWhen && dependId ? showWhen(dependValue, activeComponent?.id, ancestors) : true

        const isResponsive = items.some((setting) => hasResponsiveSettingsRecursive(setting))
        const isTable = items.some((setting) => setting.id === ComponentSettingKey.TableDataSource)

        const isListGroupColor = items.some(
          (i) =>
            i.id === ComponentSettingKey.ListDesignGroupTitleColor ||
            i.id === ComponentSettingKey.PromptDesignGroupColor
        )
        const isColorAdaptive = items.some((item) => hasColorAdaptiveSettingsRecursive(item)) || isListGroupColor
        return shouldShow ? (
          <AccordionItem
            itemId={group}
            title={t(group)}
            key={i}
            righSection={
              (isResponsive && !isColorAdaptive) || isTable ? (
                <ActiveDeviceIcon className='w-3.5 h-3.5' />
              ) : isColorAdaptive ? (
                ModeIcon
              ) : null
            }
            items={items}
          >
            {items.map((s, si) => {
              return (
                <ComponentSettingField
                  key={`${activeComponentId}-${s.id}${si}`}
                  comp={comp}
                  {...s}
                  ancestors={ancestors}
                  flattenedSettings={flattenedSettings}
                  flattenedComponents={flattenedComponents}
                />
              )
            })}
          </AccordionItem>
        ) : null
      })}
    </>
  )

  const activComponentName = accordions && accordions?.[activeComponent?.id || '']?.[name]

  return wrap ? (
    <Accordion
      key={`accordion-${activeComponent?.id}-${name}-${activComponentName || 'empty'}`}
      openMultiple={false}
      initialActiveItems={
        activeComponentAccordion
          ? [activeComponentAccordion]
          : activeComponent && accordions?.[activeComponent.id]?.[name]
            ? [accordions[activeComponent.id][name]]
            : stgs.length > 0
              ? [stgs[0][0]]
              : [] // [stgs[0][0]]
      }
      onSelect={(opt) => {
        if (activeComponent) {
          setAccordions(name, activeComponent.id, opt)
        }
      }}
    >
      {items}
    </Accordion>
  ) : (
    items
  )
}

export default ComponentSettingsAccordion
